import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DoDisturbOn, Save, ManageAccounts } from '@mui/icons-material';
import {
  Alert,
  type AlertDetails,
  Loading,
  Table,
  type HeadCell,
  IconButton,
  SelectionAdd,
} from '../../../../../../components';
import { AddPanelMemberOptions } from './options';
import { PANEL_DETAIL_MEMBER_TEXT } from '../../../../lang';
import {
  type PanelMember,
  useGetPanelMembers,
  useSearchPanelMember,
  useUpdatePanel,
} from '../../../../graphql/GSAssessment';

export type PanelMembersProps = {
  editable: boolean;
};

export const PanelMembers: React.FunctionComponent<PanelMembersProps> = (
  props: PanelMembersProps,
) => {
  const { editable } = props;
  const { panelId } = useParams();
  const [isManage, setManage] = useState(false);
  const [visibleData, setVisibleData] = useState<PanelMember[]>([]);
  const memberIds: string[] = useMemo(
    () => visibleData.map(member => member.netId),
    [visibleData],
  );
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [searchFilter, setSearchFilter] = useState<any>(null);

  const { panelMembers, loading, error, refetch } = useGetPanelMembers({
    _id: { eq: panelId },
  });

  useEffect(() => {
    if (!loading) {
      setVisibleData(
        panelMembers.map((member, index) => ({ ...member, serial: index + 1 })),
      );

      if (error instanceof Error) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    }
  }, [loading]);

  const initialDataCells: Array<HeadCell<PanelMember>> = [
    {
      id: 'order',
      label: PANEL_DETAIL_MEMBER_TEXT.NUMBER,
      isSortable: false,
      render: (row: PanelMember) => row.order,
    },
    {
      id: 'netId',
      label: PANEL_DETAIL_MEMBER_TEXT.NETID,
      isSortable: false,
      render: (row: PanelMember) => row.netId,
    },
    {
      id: 'name',
      label: PANEL_DETAIL_MEMBER_TEXT.NAME,
      isSortable: false,
      render: (row: PanelMember) => row.name,
    },
    {
      id: 'department',
      label: PANEL_DETAIL_MEMBER_TEXT.DEP,
      isSortable: false,
      render: (row: PanelMember) => row.department,
    },
    {
      id: 'email',
      label: PANEL_DETAIL_MEMBER_TEXT.EMAIL,
      isSortable: false,
      render: (row: PanelMember) => row.email,
    },
  ];

  const [dataCells, setDataCells] =
    useState<Array<HeadCell<PanelMember>>>(initialDataCells);

  const updatePanelMember = useUpdatePanel();
  const handleManage = async (): Promise<void> => {
    if (isManage) {
      setDataCells(initialDataCells);
      // Update the data to the server
      try {
        const variables = {
          input: {
            _id: panelId as string,
            panelMembers: memberIds,
          },
        };
        await updatePanelMember({
          variables,
        });
        setAlertDetails({
          alertContent: 'Data updated successfully',
          alertType: 'success',
        });
        refetch();
      } catch (error) {
        setAlertDetails({
          alertContent: error as string,
          alertType: 'error',
        });
      }
      setOpenAlert(true);
    } else {
      setDataCells([
        ...initialDataCells,
        {
          id: 'action',
          label: PANEL_DETAIL_MEMBER_TEXT.ACTION,
          isSortable: false,
          minWidth: 10,
          render: (row: PanelMember) => action(row),
        },
      ]);
    }
    setManage(!isManage);
  };

  const handleRemoveMember = (row: PanelMember) => (): void => {
    setVisibleData(prevData => {
      const updatedData = prevData.filter(member => member.netId !== row.netId);
      return updatedData.map((member, index) => ({
        ...member,
        serial: index + 1,
      }));
    });
  };

  const {
    panelMembers: searchedMembers,
    loading: searchLoading,
    error: searchError,
  } = useSearchPanelMember(searchFilter);

  useEffect(() => {
    if (!searchLoading && (searchFilter as boolean)) {
      if (searchError) {
        setAlertDetails({
          alertContent: searchError.message,
          alertType: 'error',
        });
        setOpenAlert(true);
      } else if (searchedMembers.length > 0) {
        const updatedData = [
          ...visibleData,
          {
            ...searchedMembers[0],
            serial: visibleData.length + 1,
          },
        ];
        setVisibleData(updatedData);
      } else {
        setAlertDetails({
          alertContent: 'No member found',
          alertType: 'info',
        });
        setOpenAlert(true);
      }
      setSearchFilter(null);
    }
  }, [searchLoading, searchFilter, searchedMembers, searchError]);

  const handleAddMember = (value: string, option: string): void => {
    if (memberIds.includes(value)) {
      setAlertDetails({
        alertContent: 'Member already exists',
        alertType: 'info',
      });
      setOpenAlert(true);
      return;
    }
    setSearchFilter({ netIds: [value] });
  };

  const action = (row: PanelMember): React.ReactNode => {
    return (
      <IconButton
        onClick={handleRemoveMember(row)}
        style={{ color: '#951d1d' }}
        icon={<DoDisturbOn />}
      />
    );
  };

  const handleRequestSort = (property: keyof PanelMember): void => {
    // TODO: Get the sorted data from the server
  };

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="info-area">
            <div className="table-container">
              <div className="table-area">
                <Table<PanelMember>
                  headCells={dataCells}
                  data={visibleData}
                  handleRequestSort={handleRequestSort}
                />
              </div>
              {isManage && editable && (
                <div className="selection-add-area">
                  <SelectionAdd
                    options={AddPanelMemberOptions}
                    handleAdd={handleAddMember}
                  />
                </div>
              )}
            </div>
            <div className="panel-info-container">
              {/* <PanelInfoCard /> */}
              {editable && (
                <div onClick={handleManage} className="manage-area">
                  <IconButton
                    icon={isManage ? <Save /> : <ManageAccounts />}
                    style={{ color: '#951d1d' }}
                  />
                  {isManage ? 'Save' : 'Manage'}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Container>
  );
};
const Container = styled.div`
  tr {
    width: 100%;
  }

  .switch-area {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 1rem;

    p {
      margin-left: 0.5rem;
    }
  }

  .info-area {
    width: 100%;
    height: 75vh;
    display: flex;
    flex-direction: row;

    .table-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      .table-area {
        flex: 1;
      }
      .selection-add-area {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: 50px;
      }
    }

    .panel-info-container {
      flex: 0.2;
      display: flex;
      flex-direction: column;
      justify-content: end;
    }

    .manage-area {
      display: flex;
      flex-direction: row;
      justify-content: end;
      align-items: center;
      height: 50px;
      color: #951d1d;
      div {
        cursor: pointer;
      }
    }
  }
`;
